<script lang="ts" setup>
import { ref, watch } from 'vue'
import { useHead } from '@vueuse/head'

import BackgroundWrapper from '@base/components/BackgroundWrapper/BackgroundWrapper.vue'
import ImageWrapper from '@base/components/ImageWrapper/ImageWrapper.vue'
import SplitContent from '@player/_components/SplitContent/SplitContent.vue'
import USHeroBanner from '@base/components/USHeroBanner/USHeroBanner.vue'
import USList from '@base/components/USList/USList.vue'
import USIconListItem from '@base/components/USIconListItem/USIconListItem.vue'
import USButton from '@base/components/USButton/USButton.vue'

import imageOperatorLandingHero from '@player/static/assets/activations-grants_header.jpg'
import imageOperatorLandingInfoBg from '@player/static/assets/operator-landing-info-bg.svg'
import imageHomeMembershipCtaRowImage from '@player/static/assets/activations-grants-act.png'
import imageOperatorLandingEventCards from '@player/static/assets/activations-grants-grants.png'

import { props as propDefinition } from './props'
import { Background } from '@base/_types/Background'

// Props
const props = defineProps(propDefinition)

useHead({
  title: 'Operators'
})

// Inputs
const infoRowImageProps = {
  alt: '',
  src: imageHomeMembershipCtaRowImage,
  aspectRatio: '1',
  fadeIn: true,
  lazy: true,
  objectFit: 'contain',
  objectPosition: 'center center'
}

const infoRowImagePropsSecond = {
  alt: '',
  src: imageOperatorLandingEventCards,
  aspectRatio: '1',
  fadeIn: true,
  lazy: true,
  objectFit: 'contain',
  objectPosition: 'center center'
}

const infoRowAmenities1 = [
  /*{
    icon: 'golf-club-bg',
    headline: 'Hosted Events',
    subHeadline: 'Exclusive to Course Operators',
    text: 'UpSwing organizes events each year attracting a total of 500+ players. These are not tournaments, but rather opportunities for players to enjoy playing courses they may not typically experience. Our host venues have included Banff Springs, Stewart Creek, Silvertip, Crowsnest Pass, Canmore and Sundre. Each event includes golf, carts, food & beverage, and prizing. If you would like UpSwing to host an exclusive event at your course, please contact us.',
  },*/
  {
    icon: 'target-bg',
    headline: 'Activations',
    subHeadline: 'Event Planners',
    text: 'UpSwing runs a series of activations that offer players an opportunity to test their skills, including your choice of, a Putting Contest, a Closest to the Pin Contest, or a Fairway Challenge. Activations are designed as a fun way for players to test their skills, while providing Alberta Golf with an opportunity to share information on industry initiatives. UpSwing provide the staff and bring prizing and giveaways for participants. There is no cost to the course for hosting one of these activations. If you would like UpSwing to attend one of your events please complete the enclosed application and submit it.',
    fileURL: '/2025-upswing-activation-request.pdf'
  }
]

const infoRowAmenities2 = [
  {
    icon: 'piggybank-bg',
    headline: 'Grant Program',
    subHeadline: '$20,000 in grants available - Event Planners',
    text: 'UpSwing is initiating a provincial program to promote diversity, equity, and inclusion (DEI) in golf. We are looking to support events that align with these initiatives. If you or your course host a golf event meeting our DEI criteria, UpSwing will be providing $20,000 in grants, to a maximum of $1,000 in pro shop credit per event. Apply today, grants will be awarded following the application deadline.',
    fileURL: '/2025-upswing-grant-application.pdf'
  }
]

const infoRowBg: Background = {
  fullWidth: true,
  overflow: true,
  media: [
    {
      type: 'image',
      position: {
        alignHorizontal: 'left_50',
        alignVertical: 'center',
        centerAnchor: false,
        cover: false,
        foreground: false,
        objectPositionHorizontal: 'center',
        objectPositionVertical: 'center'
      },
      imageSet: {
        mobile: {
          fadeIn: true,
          lazy: true,
          src: imageOperatorLandingInfoBg
        }
      }
    }
  ]
}
</script>

<template>
  <div class="operators-landing">
    <div class="operators-landing__hero-banner-wrapper">
      <USHeroBanner class="operators-landing__hero-banner">
        <ImageWrapper :src="imageOperatorLandingHero" class="operators-landing__hero-image"
          alt="Mother and daughter walking together" object-fit="cover" object-position="80% 33%" height="100%"
          width="100%" :fade-in="false" :lazy="false" />
      </USHeroBanner>
    </div>
    <div class="operators-landing__info-row margin-top--3">
      <BackgroundWrapper :background="infoRowBg" />
      <SplitContent>
        <template #left>
          <div class="operators-landing__info-row-content">
            <h2 class="operators-landing__info-row-heading heading--h3">
              Get your golf course and events discovered by the latest segment of the Alberta golf market.
            </h2>
            <p class="operators-landing__info-row-body body--p1--regular margin-top--8">
              Operators and event planners receive complimentary access to the UpSwing platform to showcase and promote
              their events.
            </p>
            <div class=""></div>
            <USList type="icon" class="operators-landing__info-row-amenities">
              <USIconListItem v-for="(amenity, idx) in infoRowAmenities1" :key="idx" :icon="amenity.icon"
                color="var(--font-color--primary-b1)" css-diameter="var(--amenity-icon-diameter)">
                <h3 class="amenity__heading heading--h3 margin-bottom--9">{{ amenity.headline }}</h3>
                <h4 class="amenity__sub-heading heading--h4 margin-bottom--9">{{ amenity.subHeadline }}</h4>
                <p class="body--p1--regular margin-bottom--5">{{ amenity.text }}</p>
                <a :href="amenity.fileURL" target="_blank" rel="noopener noreferrer">
                  <USButton label="Apply Here" type="primary" />
                </a>
              </USIconListItem>
            </USList>
          </div>
        </template>
        <template #right>
          <ImageWrapper v-bind="infoRowImageProps" class="operators-landing__info-row-image" />
        </template>
      </SplitContent>
    </div>
    <div class="operators-landing__info-row margin-top--5">
      <SplitContent>
        <template #left>
          <ImageWrapper v-bind="infoRowImagePropsSecond" class="operators-landing__info-row-image" />
        </template>
        <template #right>
          <div class="operators-landing__info-row-content">
            <USList type="icon" class="operators-landing__info-row-amenities amenities-2">
              <USIconListItem v-for="(amenity, idx) in infoRowAmenities2" :key="idx" :icon="amenity.icon"
                color="var(--font-color--primary-b1)" css-diameter="var(--amenity-icon-diameter)">
                <h3 class="amenity__heading heading--h3 margin-bottom--9">{{ amenity.headline }}</h3>
                <h4 class="amenity__sub-heading heading--h4 margin-bottom--9">{{ amenity.subHeadline }}</h4>
                <p class="body--p1--regular margin-bottom--5">{{ amenity.text }}</p>
                <a :href="amenity.fileURL" target="_blank" rel="noopener noreferrer">
                  <USButton label="Apply Here" type="primary" />
                </a>
              </USIconListItem>
            </USList>
          </div>
        </template>
      </SplitContent>
    </div>
    <div class="operators-landing__pre-footer grid__row">
      <div class="grid__column grid__column--12 text-align--center">
        <h2 class="operators-landing__pre-footer-heading display--d1 margin-top--7">
          Find your fun.
        </h2>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '@base/styles/v1.0/scss/foundations/breakpoint' as bp;

$amenity-icon-diameter-mobile: 30px;
$amenity-icon-diameter-tablet: 38px;
$amenity-icon-diameter-desktop: 44px;

.operators-landing {
  --amenity-icon-diameter: #{$amenity-icon-diameter-mobile};
  margin-top: calc(-1 * var(--spacing--5));

  @include bp.min-width(bp.$breakpoint--tablet) {
    --amenity-icon-diameter: #{$amenity-icon-diameter-tablet};
  }

  @include bp.min-width(bp.$breakpoint--desktop) {
    --amenity-icon-diameter: #{$amenity-icon-diameter-desktop};
  }

  &__info-row-heading {
    text-align: center;

    @include bp.min-width(bp.$breakpoint--tablet) {
      text-align: left;
    }
  }

  &__info-row-body {
    text-align: center;

    @include bp.min-width(bp.$breakpoint--tablet) {
      text-align: left;
    }
  }

  &__info-row-link {
    margin-bottom: var(--spacing--template);
    text-align: center;

    @include bp.min-width(bp.$breakpoint--7) {
      margin-bottom: 0;
      text-align: left;
    }
  }

  &__info-row-amenities {
    columns: 2;
    padding-left: 0 !important;
    padding-right: 0 !important;

    li {
      align-items: flex-start;
    }

    .amenity__heading,
    .amenity__sub-heading {
      color: var(--color--primary-b1);
    }
    
  }

  &__info-row-button {
    text-align: center;

    @include bp.min-width(bp.$breakpoint--7) {
      text-align: left;
    }
  }

  &__pre-footer {
    margin-bottom: var(--spacing--template);
    margin-top: var(--spacing--template);

    &-heading {
      color: var(--color--secondary-c3);
    }
  }
}
</style>
